import React from "react";
import { useTranslation } from "react-i18next";
import { FileUploader } from "../../common/FileUploader";
import { S3_BASE_URL } from "../../../utils/Constants";
import { Palette, Image, Upload, Info, Eye } from 'lucide-react';

export default function BrandingTab({ formData, onInputChange }) {
  const { t } = useTranslation();

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="bg-gradient-to-r from-pink-50 to-purple-50 rounded-xl p-6 border border-pink-100">
        <div className="flex items-center mb-4">
          <div className="p-3 bg-pink-100 rounded-lg mr-4">
            <Palette className="w-6 h-6 text-pink-600" />
          </div>
          <div>
            <h3 className="text-xl font-semibold text-gray-900">{t('settings.tabs.branding')}</h3>
            <p className="text-sm text-gray-600">{t('settings.branding.description')}</p>
          </div>
        </div>
      </div>

      {/* Restaurant Image */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
          <h4 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <Image className="w-5 h-5" />
            {t("settings.restaurantImage")}
          </h4>
          <p className="text-sm text-gray-600 mt-1">{t('settings.restaurantImageDescription')}</p>
        </div>
        
        <div className="p-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div>
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 hover:border-pink-400 transition-colors">
                <FileUploader
                  onUploadComplete={(key) => onInputChange("imageUrl", key)}
                  previewEnabled={true}
                  initialKey={formData.imageUrl}
                  uploadingText="settings.uploading"
                  t={t}
                />
              </div>
            </div>

            {formData.imageUrl ? (
              <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                <div className="bg-gray-100 px-4 py-2 border-b border-gray-200">
                  <h5 className="text-sm font-medium text-gray-700 flex items-center gap-2">
                    <Eye className="w-4 h-4" />
                    {t("settings.currentImage")}
                  </h5>
                </div>
                <div className="p-4">
                  <img
                    src={
                      formData.imageUrl.startsWith("http")
                        ? formData.imageUrl
                        : `${S3_BASE_URL}/${formData.imageUrl}`
                    }
                    alt={t("settings.restaurantImage")}
                    className="rounded-lg w-full h-48 object-cover shadow-md"
                    onError={(e) => {
                      e.target.style.display = "none";
                    }}
                  />
                </div>
              </div>
            ) : (
              <div className="bg-gray-50 rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                <div className="p-8 text-center">
                  <Image className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                  <h5 className="text-lg font-medium text-gray-900 mb-2">{t('settings.noImageUploaded')}</h5>
                  <p className="text-gray-600">{t('settings.uploadImageToSeePreview')}</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Background Image */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
          <h4 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <Upload className="w-5 h-5" />
            {t("settings.backgroundImage")}
          </h4>
          <p className="text-sm text-gray-600 mt-1">{t('settings.backgroundImageDescription')}</p>
        </div>
        
        <div className="p-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div>
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 hover:border-pink-400 transition-colors">
                <FileUploader
                  onUploadComplete={(key) =>
                    onInputChange("backgroundImageUrl", key)
                  }
                  previewEnabled={true}
                  initialKey={formData.backgroundImageUrl}
                  uploadingText="settings.uploading"
                  t={t}
                />
              </div>
            </div>

            {formData.backgroundImageUrl ? (
              <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                <div className="bg-gray-100 px-4 py-2 border-b border-gray-200">
                  <h5 className="text-sm font-medium text-gray-700 flex items-center gap-2">
                    <Eye className="w-4 h-4" />
                    {t("settings.currentBackgroundImage")}
                  </h5>
                </div>
                <div className="p-4">
                  <img
                    src={
                      formData.backgroundImageUrl.startsWith("http")
                        ? formData.backgroundImageUrl
                        : `${S3_BASE_URL}/${formData.backgroundImageUrl}`
                    }
                    alt={t("settings.backgroundImage")}
                    className="rounded-lg w-full h-48 object-cover shadow-md"
                    onError={(e) => {
                      e.target.style.display = "none";
                    }}
                  />
                </div>
              </div>
            ) : (
              <div className="bg-gray-50 rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                <div className="p-8 text-center">
                  <Upload className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                  <h5 className="text-lg font-medium text-gray-900 mb-2">{t('settings.noBackgroundUploaded')}</h5>
                  <p className="text-gray-600">{t('settings.uploadBackgroundToSeePreview')}</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Image Guidelines */}
      <div className="bg-white rounded-xl shadow-sm border border-blue-200 overflow-hidden">
        <div className="bg-blue-50 px-6 py-4 border-b border-blue-200">
          <h4 className="text-lg font-semibold text-blue-900 flex items-center gap-2">
            <Info className="w-5 h-5" />
            {t("settings.imageGuidelines")}
          </h4>
        </div>
        
        <div className="p-6">
          <div className="space-y-3">
            <div className="flex items-start gap-3">
              <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0"></div>
              <p className="text-sm text-blue-700">{t("settings.imageGuidelineSize")}</p>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0"></div>
              <p className="text-sm text-blue-700">{t("settings.imageGuidelineFormat")}</p>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0"></div>
              <p className="text-sm text-blue-700">{t("settings.imageGuidelineQuality")}</p>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0"></div>
              <p className="text-sm text-blue-700">{t("settings.imageGuidelineContent")}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
